import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Descriptions, 
  Spin, 
  message, 
  Button,
  Image,
  Row,
  Col
} from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { LeftOutlined } from '@ant-design/icons';
import shelfService from '../../services/shelfService';
import { Shelf } from '../../types/shelf';
import { formatTimestamp } from '../../utils/responseTransformer';

const ShelfDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [shelf, setShelf] = useState<Shelf | null>(null);

  useEffect(() => {
    if (id) {
      fetchShelfDetail(parseInt(id, 10));
    }
  }, [id]);

  const fetchShelfDetail = async (shelfId: number) => {
    setLoading(true);
    try {
      const response = await shelfService.getShelfById(shelfId);
      
      if (response.success && response.data) {
        setShelf(response.data);
      } else {
        message.error(response.errorMessage || t('shelf.getDetailFailed'));
      }
    } catch (error) {
      console.error('获取货架详情失败:', error);
      message.error(t('shelf.getDetailFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/shelf');
  };

  if (loading) {
    return (
      <div className="page-loading">
        <Spin size="large" />
      </div>
    );
  }

  if (!shelf) {
    return (
      <Card className="detail-card">
        <div className="no-data">{t('common.noData')}</div>
      </Card>
    );
  }

  return (
    <div className="shelf-detail">
      <Card className="page-header-card">
        <div className="page-header">
          <Button 
            icon={<LeftOutlined />} 
            onClick={handleBack}
            className="back-button"
          >
            {t('common.back')}
          </Button>
          <div className="page-title">{t('shelf.detailTitle')}</div>
        </div>
      </Card>

      <Card className="detail-card">
        <Descriptions 
          title={t('shelf.basicInfo')} 
          column={2}
          bordered
        >
          <Descriptions.Item label={t('shelf.id')}>
            {shelf.id}
          </Descriptions.Item>
          <Descriptions.Item label={t('shelf.warehouseCode')}>
            {shelf.warehouseCode}
          </Descriptions.Item>
          <Descriptions.Item label={t('shelf.code')}>
            {shelf.code}
          </Descriptions.Item>
          <Descriptions.Item label={t('shelf.sectionTypeCode')}>
            {shelf.sectionTypeCode}
          </Descriptions.Item>
           <Descriptions.Item label={t('shelf.sectionTypeName')}>
            {shelf.sectionTypeName}
          </Descriptions.Item>
          <Descriptions.Item label={t('shelf.displayGroupCode')}>
            {shelf.displayGroupCode || '-'}
          </Descriptions.Item>
          <Descriptions.Item label={t('shelf.displayGroupIndex')}>
            {shelf.displayGroupIndex || '-'}
          </Descriptions.Item>
          <Descriptions.Item label={t('shelf.isMirrored')}>
            {shelf.isMirrored ? t('common.yes') : t('common.no')}
          </Descriptions.Item>
          <Descriptions.Item label={t('shelf.allowOverlap')}>
            {shelf.allowOverlap ? t('common.yes') : t('common.no')}
          </Descriptions.Item>
          <Descriptions.Item label={t('shelf.creatorName')}>
            {shelf.creatorName || '-'}
          </Descriptions.Item>
          <Descriptions.Item label={t('shelf.modifierName')}>
            {shelf.modifierName || '-'}
          </Descriptions.Item>
          <Descriptions.Item label={t('shelf.gmtCreate')}>
            {formatTimestamp(shelf.gmtCreate)}
          </Descriptions.Item>
          <Descriptions.Item label={t('shelf.gmtModified')}>
            {formatTimestamp(shelf.gmtModified)}
          </Descriptions.Item>
        </Descriptions>

        <Descriptions 
          title={t('shelf.dimensionsInfo')} 
          column={2}
          bordered
          style={{ marginTop: 24 }}
        >
          <Descriptions.Item label={t('shelf.outerLength')}>
            {shelf.outerLength || '-'}
          </Descriptions.Item>
          <Descriptions.Item label={t('shelf.outerWidth')}>
            {shelf.outerWidth || '-'}
          </Descriptions.Item>
          <Descriptions.Item label={t('shelf.outerHeight')}>
            {shelf.outerHeight || '-'}
          </Descriptions.Item>
          <Descriptions.Item label={t('shelf.innerLength')}>
            {shelf.innerLength || '-'}
          </Descriptions.Item>
          <Descriptions.Item label={t('shelf.innerWidth')}>
            {shelf.innerWidth || '-'}
          </Descriptions.Item>
          <Descriptions.Item label={t('shelf.innerHeight')}>
            {shelf.innerHeight || '-'}
          </Descriptions.Item>
          <Descriptions.Item label={t('shelf.layerCount')}>
            {shelf.layerCount || '-'}
          </Descriptions.Item>
        </Descriptions>

        {shelf.sectionTypeImageUrl && (
          <div style={{ marginTop: 24 }}>
            <h3>{t('shelf.sectionTypeImage')}</h3>
            <Image
              src={shelf.sectionTypeImageUrl}
              alt={t('shelf.sectionTypeImage')}
              width={200}
              height={200}
            />
          </div>
        )}
      </Card>
    </div>
  );
};

export default ShelfDetail;